import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import theme from '../../styles/theme';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  margin-top: ${theme.spacing[6]};
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const ItemsPerPageSelect = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[500]}20;
  }
`;

const ItemsLabel = styled.span`
  font-weight: 500;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PageButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${props => (props.$active ? theme.colors.primary[500] : 'transparent')};
  color: ${props => (props.$active ? theme.colors.text.primary : theme.colors.text.secondary)};
  font-size: ${theme.fontSizes.sm};
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => (props.$active ? theme.colors.primary[600] : theme.colors.background.primary)};
    color: ${props => (props.$active ? theme.colors.text.primary : theme.colors.primary[500])};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const NavButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  padding: 0 ${theme.spacing[2]};
  color: ${theme.colors.text.muted};
`;

const ClienteSobrePaginacao = ({
  pagination,
  onPageChange,
  onItemsPerPageChange,
  loading,
}) => {
  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPrevPage,
  } = pagination;

  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
      onPageChange(page);
    }
  };

  const changeItemsPerPage = (event) => {
    const value = Number.parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value !== itemsPerPage) {
      onItemsPerPageChange(value);
    }
  };

  const buildPages = () => {
    const windowSize = 2;
    const pages = [];

    for (
      let i = Math.max(2, currentPage - windowSize);
      i <= Math.min(totalPages - 1, currentPage + windowSize);
      i += 1
    ) {
      pages.push(i);
    }

    const full = [];
    if (currentPage - windowSize > 2) {
      full.push(1, '...');
    } else {
      for (let i = 1; i < Math.max(2, currentPage - windowSize); i += 1) {
        full.push(i);
      }
    }

    full.push(...pages);

    if (currentPage + windowSize < totalPages - 1) {
      full.push('...', totalPages);
    } else {
      for (let i = Math.max(currentPage + windowSize + 1, totalPages - 1); i <= totalPages; i += 1) {
        if (i >= 1) {
          full.push(i);
        }
      }
    }

    return full.filter((value, index, array) => {
      if (value === '...') {
        return array[index - 1] !== '...' && array[index + 1] !== '...';
      }
      return array.indexOf(value) === index;
    });
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Container>
      <Info>
        <ItemsLabel>Registros por página:</ItemsLabel>
        <ItemsPerPageSelect value={itemsPerPage} onChange={changeItemsPerPage} disabled={loading}>
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </ItemsPerPageSelect>
        <span>
          Mostrando {startItem} - {endItem} de {totalItems} registros
        </span>
      </Info>

      <Controls>
        <NavButton
          onClick={() => handlePageChange(1)}
          disabled={!hasPrevPage || loading}
          title="Primeira página"
        >
          <FiChevronsLeft />
        </NavButton>
        <NavButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPrevPage || loading}
          title="Página anterior"
        >
          <FiChevronLeft />
        </NavButton>

        {buildPages().map((page, index) => {
          if (page === '...') {
            return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
          }
          return (
            <PageButton
              key={page}
              $active={page === currentPage}
              onClick={() => handlePageChange(page)}
              disabled={loading}
            >
              {page}
            </PageButton>
          );
        })}

        <NavButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage || loading}
          title="Próxima página"
        >
          <FiChevronRight />
        </NavButton>
        <NavButton
          onClick={() => handlePageChange(totalPages)}
          disabled={!hasNextPage || loading}
          title="Última página"
        >
          <FiChevronsRight />
        </NavButton>
      </Controls>
    </Container>
  );
};

export default ClienteSobrePaginacao;


